export interface ApiResponse {
  statusCode: number;
  headers: Record<string, string>;
  body: string;
}

export interface AppResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

/**
 * Mirrors the `plan_type` enum in supabase-schema.sql.
 *
 * `pro_trial` and `pro` are retired but not removed: Postgres cannot drop an
 * enum label while rows reference it, and subscribers on those plans are still
 * mid-period. TIER_LIMITS maps them onto the starter/growth limits so nobody's
 * plan silently changes shape under them.
 */
export type PlanType =
  'free_trial' | 'starter' | 'growth' | 'agency' | 'institution' | 'pro_trial' | 'pro';

export type SubscriptionStatus = 'active' | 'expired' | 'cancelled';

/**
 * What a plan is allowed to do. Every field is a hard limit enforced on the
 * backend — the frontend reads these to disable controls early, but a client
 * that ignores them still gets a 403.
 */
export interface TierLimits {
  brands: number;
  /** Connected social accounts, summed across every platform. */
  connectedAccounts: number;
  /** Counted when a post actually publishes, not when it is scheduled. */
  postsPerMonth: number;
  aiGenerationsPerMonth: number;
  mediaStorageMb: number;
  /** How far into the future a post may be scheduled. */
  scheduleHorizonDays: number;
  approvalWorkflow: boolean;
  analyticsRetentionDays: number;
  /** Platforms this plan may connect at all. */
  platforms: readonly SocialPlatform[];
}

export interface UserSubscription {
  plan: PlanType;
  status: SubscriptionStatus;
  periodStart: string;
  periodEnd: string;
  postsPublished: number;
  postsRemaining: number;
  aiGenerations: number;
  aiGenerationsRemaining: number;
  mediaBytes: number;
  limits: TierLimits;
  hasUsedFreeTrial: boolean;
}

// ─── Social domain ────────────────────────────────────────────────────────────
// Each of the four enums below mirrors a Postgres enum in supabase-schema.sql.
// Changing one requires changing the other, in the order the migration notes
// spell out: the label must be committed to Postgres before any code inserts it.

export const SOCIAL_PLATFORMS = ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export const INDUSTRY_VERTICALS = [
  'restaurant',
  'dental',
  'real_estate',
  'cleaning',
  'agency',
  'other',
] as const;
export type IndustryVertical = (typeof INDUSTRY_VERTICALS)[number];

/**
 * The post lifecycle.
 *
 * `publishing` is a lease, not a user-visible state: the scheduler flips a
 * variant into it to claim the row, and either the publisher resolves it or the
 * stale-claim sweeper returns it to `scheduled`.
 */
export const POST_STATUSES = [
  'draft',
  'pending_review',
  'approved',
  'scheduled',
  'publishing',
  'published',
  'failed',
  'cancelled',
] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

/**
 * `needs_reauth` is a first-class state rather than an error. Meta revokes page
 * tokens on password and permission changes, TikTok rotates its refresh token on
 * every use, and YouTube expires refresh tokens weekly while its consent screen
 * sits in Testing. A dead token stops a customer's whole calendar, so it has to
 * be something the UI can show rather than something discovered at 6am.
 */
export const SOCIAL_ACCOUNT_STATUSES = ['active', 'needs_reauth', 'revoked', 'disabled'] as const;
export type SocialAccountStatus = (typeof SOCIAL_ACCOUNT_STATUSES)[number];

export type BrandTone = 'playful' | 'professional' | 'warm' | 'authoritative' | 'luxury';

/**
 * The business context every generation reads.
 *
 * Persisted as JSONB on `brands.profile` rather than as columns, because it is
 * always read whole, never queried field-by-field, and its shape changes with
 * prompt engineering rather than with migrations. It is also the cached prompt
 * prefix on every model call — stable per brand, and large enough that caching
 * it pays for itself immediately.
 */
export interface BrandProfile {
  tagline?: string;
  description?: string;
  services: string[];
  tone: BrandTone;
  audience?: string;
  locality?: string;
  /**
   * Language the *content* is generated in. Deliberately separate from the UI
   * language: a Bengali-speaking owner may well post in English, and switching
   * the interface must never silently change what goes out to their audience.
   */
  languages: string[];
  offers: string[];
  brandVoiceNotes?: string;
  bannedPhrases: string[];
  callsToAction: string[];
  links: { website?: string; booking?: string; menu?: string };
  hashtagSets: { core: string[]; rotating: string[]; local: string[] };
  colorHex?: string;
  logoAssetId?: string;
}

export interface Brand {
  id: string;
  name: string;
  vertical: IndustryVertical;
  profile: BrandProfile;
  timezone: string;
  approvalRequired: boolean;
  createdAt: string;
}

/** The fields required to create a new brand. */
export interface CreateBrandInput {
  name: string;
  vertical: IndustryVertical;
  /** IANA timezone string, e.g. "Asia/Dhaka". */
  timezone: string;
}

export type MediaAssetStatus = 'pending' | 'ready' | 'archived';
export type MediaAssetType = 'image' | 'video';

/**
 * A media asset as the frontend is allowed to see it.
 *
 * The S3 key, encryption details and raw CDN path never leave the backend —
 * `url` is a signed or public CDN URL the client can render directly.
 *
 * `ownerId` / `ownerEmail` are present only in the admin projection so a
 * superadmin can see who uploaded a file without joining to auth.users on the
 * client. Creators receive these as null.
 */
export interface MediaAsset {
  id: string;
  ownerId: string;
  ownerEmail: string | null;
  name: string;
  mimeType: string;
  type: MediaAssetType;
  sizeBytes: number;
  url: string;
  thumbnailUrl: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  altText: string | null;
  status: MediaAssetStatus;
  /** User IDs this asset has been explicitly shared with by an admin. */
  sharedWith: string[];
  createdAt: string;
}

export interface MediaUploadUrlResponse {
  mediaId: string;
  uploadUrl: string;
  /** Extra form fields required by the presigned POST (AWS S3 multipart). */
  fields: Record<string, string>;
}

/**
 * A connected publishing destination, as the frontend is allowed to see it.
 *
 * Deliberately a redacted projection of `social_accounts`: the token ciphertext,
 * wrapped data key and key id exist on the row but must never cross the wire,
 * and there is no client feature that needs them.
 */
export interface SocialAccountSummary {
  id: string;
  brandId: string;
  platform: SocialPlatform;
  externalId: string;
  externalName: string | null;
  avatarUrl: string | null;
  status: SocialAccountStatus;
  statusReason: string | null;
  accessExpiresAt: string | null;
}

export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'cancelled';

/** Billing currency. Drives which gateway a checkout is routed to. */
export type Currency = 'BDT' | 'USD';

export interface PaymentRecord {
  id: string;
  tranId: string;
  plan: PlanType;
  amount: number;
  status: PaymentStatus;
  createdAt: string;
}

export interface InitiatePaymentResponse {
  tranId: string;
  activated: boolean;
  gatewayUrl?: string;
}

export interface InitiatePaymentResult {
  tranId: string;
  activated: boolean;
  gatewayUrl?: string;
}

// ─── Roles ────────────────────────────────────────────────────────────────────

/**
 * Held in the Supabase user's `app_metadata`, which only the service-role key and
 * the dashboard can write — a user cannot promote themselves. Never read this
 * from `user_metadata`, which is self-writable and therefore worthless for
 * authorization. A missing role means `'user'`.
 */
export type UserRole = 'user' | 'superadmin';

export const SUPERADMIN_ROLE: UserRole = 'superadmin';

// ─── Audit log ────────────────────────────────────────────────────────────────

/**
 * Mirrors the `activity_type` enum in supabase-schema.sql. Change both together.
 *
 * ADD labels here, never remove them. `activity_log` is append-only and holds
 * years of rows; `question_generated` is retired in favour of
 * `content_generated`, but dropping it would break the admin feed's rendering of
 * every historical row and reset lifetime counters to zero.
 */
export const ACTIVITY_TYPES = [
  'user_signed_up',
  'user_signed_in',
  'content_generated',
  'generation_blocked',
  'generation_failed',
  'brand_created',
  'account_connected',
  'account_disconnected',
  'account_reauth_required',
  'post_scheduled',
  'post_published',
  'post_failed',
  'post_approved',
  'post_rejected',
  'token_refresh_failed',
  'payment_initiated',
  'payment_completed',
  'payment_failed',
  'subscription_activated',
  'subscription_cancelled',
  'user_banned',
  'user_unbanned',
  /** Retired: the question-generator era. Kept so old rows still render. */
  'question_generated',
] as const;

export type ActivityType = (typeof ACTIVITY_TYPES)[number];

/**
 * Free-form per-event detail. Deliberately metadata only — no caption, post body
 * or media ever goes in here, and failures carry an upstream status code without
 * the upstream message, which can quote our API key or an access token back at
 * us.
 */
export type ActivityMetadata = Record<string, string | number | boolean | string[] | null>;

export interface ActivityLogEntry {
  id: string;
  userId: string | null;
  userEmail: string | null;
  type: ActivityType;
  metadata: ActivityMetadata;
  createdAt: string;
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminUserSummary {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  role: UserRole;
  plan: PlanType | null;
  status: SubscriptionStatus | null;
  periodEnd: string | null;
  createdAt: string;
  lastSignInAt: string | null;
  totalGenerations: number;
  /**
   * `auth.users.banned_until`, the authoritative ban flag. Null when never
   * banned; a past timestamp when a ban has since expired. Distinct from
   * `status` above, which is the *subscription* lifecycle.
   */
  bannedUntil: string | null;
}

// ─── Bans ─────────────────────────────────────────────────────────────────────

/**
 * Ban durations are stored in GoTrue as a Go duration string. An indefinite ban
 * is expressed as ~100 years rather than a null, because `banned_until` is the
 * only field the auth layer checks — there is no separate "forever" flag.
 */
export const INDEFINITE_BAN_HOURS = 876_000;

/** Anything expiring beyond this is a `INDEFINITE_BAN_HOURS` ban, not a real date. */
export const INDEFINITE_BAN_THRESHOLD_MS = 50 * 365 * 24 * 60 * 60 * 1000;

export function isIndefiniteBan(bannedUntil: string): boolean {
  return new Date(bannedUntil).getTime() - Date.now() > INDEFINITE_BAN_THRESHOLD_MS;
}

/** True only for a ban that is still in force — an expired `banned_until` is not a ban. */
export function isBanActive(bannedUntil: string | null | undefined): boolean {
  return Boolean(bannedUntil && new Date(bannedUntil).getTime() > Date.now());
}

/**
 * Upper bound on a fixed-length ban. Anything longer is an indefinite ban, which
 * is expressed by omitting `days` rather than by asking for 10,000 of them.
 */
export const BAN_MAX_DAYS = 365;
export const BAN_REASON_MAX_LENGTH = 500;

// ─── Renewal reminders ────────────────────────────────────────────────────────

/**
 * How long an expired subscription stays quiet after a reminder before prompting
 * again. There is no cap on the number of reminders: an account that stays
 * expired keeps being asked, because the prompt stops the moment it works.
 */
export const RENEWAL_REMINDER_INTERVAL_DAYS = 15;

export const RENEWAL_REMINDER_INTERVAL_MS = RENEWAL_REMINDER_INTERVAL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Whether an expired subscription is due to prompt the user to subscribe again.
 *
 * Deliberately measured from the last reminder rather than from `period_end`, so a
 * dismissal always buys a full quiet interval. Anchoring to expiry instead would
 * let someone who dismissed on day 14 be prompted again on day 15.
 *
 * Lives in `common/` because it is the whole rule: whatever delivers the reminder
 * — today the dashboard banner, later a scheduled sweep — must agree on when one
 * is owed, and a second copy of this arithmetic is how the two would drift apart.
 */
export function isRenewalReminderDue(
  status: SubscriptionStatus,
  lastRemindedAt: string | null | undefined,
  now: number = Date.now()
): boolean {
  if (status !== 'expired') return false;
  if (!lastRemindedAt) return true;

  const remindedAt = new Date(lastRemindedAt).getTime();
  // An unparseable timestamp is treated as "never reminded". The alternative is a
  // NaN comparison, which is always false — silently muting the prompt forever.
  if (Number.isNaN(remindedAt)) return true;

  return now - remindedAt >= RENEWAL_REMINDER_INTERVAL_MS;
}

export interface BanUserRequest {
  reason: string;
  /** Omitted means indefinite. */
  days?: number;
}

export interface ActiveBan {
  reason: string | null;
  bannedUntil: string | null;
}

/** The 403 body a banned caller gets from every authenticated endpoint. */
export const ACCOUNT_BANNED_ERROR = 'ACCOUNT_BANNED';

/** Shape of the `admin_stats(days)` RPC's JSONB. Change both together. */
export interface AdminStats {
  totalUsers: number;
  newUsers: number;
  activeSubscriptions: number;
  totalGenerations: number;
  generationsInPeriod: number;
  totalPostsPublished: number;
  postsPublishedInPeriod: number;
  connectedAccounts: number;
  /**
   * Accounts whose token died and that the customer has not reconnected. The one
   * number here worth alerting on: it is silent churn, because a brand in this
   * state has a calendar that quietly stops publishing.
   */
  accountsNeedingReauth: number;
  revenueInPeriod: number;
  planDistribution: Partial<Record<PlanType, number>>;
  platformDistribution: Partial<Record<SocialPlatform, number>>;
  activityByType: Partial<Record<ActivityType, number>>;
  generationsPerDay: { date: string; count: number }[];
  postsPublishedPerDay: { date: string; count: number }[];
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
